import type { ReactNode } from "react"

// The modal shell both agent dialogs sit in.
//
// It exists because QuestionDialog and PermissionDialog each hand-rolled the
// same one, character for character, and it was wrong in the same way in both:
// centred with `items-center` and carrying no max-height and no overflow, so a
// tall card grew off the top and bottom of the viewport with no way to scroll
// to it. A question set of any size put its own Submit button out of reach.
//
// ui.tsx's Modal already solves the layout — `items-start`, `overflow-y-auto`,
// `pt-[10vh]` — and this is that, with three differences. It is wider
// (max-w-2xl, the width both dialogs already used), it has no close button, and
// it does not call useDismiss: these block on purpose, and leaving one is an
// explicit choice, not an outside click.
export function AgentDialog({
  title,
  headerActions,
  footer,
  children,
}: {
  title: string
  headerActions?: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-[10vh]">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // max-h/flex-col with a scrolling body rather than a scrolling page: the
        // header keeps the step indicator visible and the footer keeps Submit
        // reachable, which is the whole complaint.
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-surface shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border p-5 pb-4">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          {headerActions}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

        {footer && <div className="flex items-center gap-2 border-t border-border p-5 pt-4">{footer}</div>}
      </div>
    </div>
  )
}
